// Desc: Home page component
import { SearchBox } from "_components";
import { cx, formatCurrency } from "_lib/utils";
import { ConfigurationAtom } from "_states";
import { Link } from "react-router-dom";
import { useRecoilValue } from "recoil";

/**
 * HomePage component
 * @returns {JSX.Element} JSX.Element
 */
const HomePage = () => {
  const configurations = useRecoilValue(ConfigurationAtom);

  return (
    <>
      <div
        className="flex flex-col items-center justify-center lg:h-[700px] h-[500px] bg-center bg-cover bg-no-repeat w-full text-white lg:space-y-6 space-y-4 text-center"
        style={{ backgroundImage: "url('/images/banner.jpg')" }}
      >
        <h1 className="lg:text-6xl text-2xl px-2">
          Explore the world together
        </h1>
        <p className="lg:text-2xl pb-6">
          Find awesome flights, hotel, tour, car and packages
        </p>
        <SearchBox />
      </div>

      {/* Top Destinations section */}
      <section className="max-w-7xl mx-auto space-y-8 py-16 md:py-36 md:px-2 sm:px-4 px-4">
        <div>
          <h2 className="font-semibold md:text-4xl text-2xl text-center mb-10">
            Top destinations
          </h2>
          <div className="grid lg:grid-cols-6 md:grid-cols-3 sm:grid-cols-2 grid-cols-1 grid-rows-3 gap-6">
            {configurations.featured_destinations
              .slice(0, 12)
              .map((destination, index) => (
                <Link
                  key={index}
                  to={`/tours/${destination.slug}`}
                  className={cx(
                    "relative flex justify-center items-center rounded-lg overflow-hidden aspect-video md:aspect-square",
                    index === 0 || index === 7
                      ? "lg:col-span-2 lg:row-span-2"
                      : ""
                  )}
                >
                  <div
                    className="h-full w-full transition ease-in-out duration-500 hover:scale-110 bg-center bg-cover brightness-75 hover:brightness-50"
                    style={{
                      backgroundImage: `url('${destination.image}')`,
                    }}
                  ></div>
                  <h3
                    className={cx(
                      "text-white font-medium absolute pointer-events-none",
                      index == 0 || index == 7
                        ? "lg:text-3xl text-2xl"
                        : "text-2xl"
                    )}
                  >
                    {destination.name}
                  </h3>
                </Link>
              ))}
          </div>
        </div>
        <div className="text-center">
          <Link
            to="/destinations"
            className="bg-primary text-white font-medium px-8 py-3 rounded-lg"
          >
            View all destinations
          </Link>
        </div>
      </section>

      {/* Popular tours section */}
      <section className="max-w-7xl mx-auto space-y-8 pb-24 md:px-2 sm:px-4 px-4">
        <div>
          <h2 className="font-semibold md:text-4xl text-2xl text-center mb-10">
            Our popular tours
          </h2>
          <div className="grid md:grid-cols-4 sm:grid-cols-2 gap-6">
            {configurations.featured_tours.slice(0, 8).map((tour) => (
              <Link
                key={tour.id}
                to={`/itinerary/${tour.id}`}
                className="bg-white border transition ease-in-out duration-200 hover:shadow-lg hover:scale-105 rounded-lg overflow-hidden"
              >
                <img
                  src={tour.image ? tour.image : "/images/tour.png"}
                  alt="tour"
                  className="w-full h-52 object-cover object-center"
                />
                <div className="p-4 space-y-2">
                  <h3 className="text-xl font-semibold">{tour.name}</h3>
                  <p className="text-sm">
                    {tour.nights + 1} days {tour.nights} nights
                  </p>
                  <p className="text-sm">
                    Starting from{" "}
                    <strong>{formatCurrency(tour.starting_price)}</strong> pax
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
        <div className="text-center">
          <Link
            to="/tours"
            className="bg-primary text-white font-medium px-8 py-3 rounded-lg"
          >
            View all tours
          </Link>
        </div>
      </section>
    </>
  );
};

export default HomePage;
